import { Phone, MessageCircle, Star, Flame } from "lucide-react";
import { PHONE_TEL, WA_LINK } from "../data/menuData";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative bg-[#FAFAFA] grain-bg border-b-2 border-black overflow-hidden"
    >
      {/* Marquee strip */}
      <div className="bg-[#111111] text-[#FFB800] py-2 overflow-hidden border-b-2 border-black">
        <div className="marquee-track gap-12 whitespace-nowrap font-display text-lg md:text-xl">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-12 pr-12">
              <span>🍗 CRISPY • JUICY • TASTY</span>
              <span>•</span>
              <span>HOME DELIVERY MIN ₹300 + ₹45 DELIVERY • PARCEL ₹10 EXTRA</span>
              <span>•</span>
              <span>FREE STREET PARKING</span>
              <span>•</span>
              <span>DINE-IN • TAKEAWAY • DELIVERY</span>
              <span>•</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-16 py-12 md:py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
        {/* Left content */}
        <div className="lg:col-span-7 fade-up">
          {/* Logo */}
          <div className="mb-6">
            <img
              src="/assets/WhatsApp Image 2026-05-07 at 7.10.03 PM (1).jpeg"
              alt="KKFC Logo"
              className="w-[35%] max-w-xs h-auto object-contain"
              loading="eager"
            />
          </div>

          <div className="inline-flex items-center gap-2 brutal-border bg-[#FFB800] text-black px-3 py-1 text-xs font-bold uppercase tracking-widest mb-5">
            <Flame size={14} strokeWidth={3} />
            Bangarpet's #1 Chicken Spot · 100% Halal
          </div>

          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight uppercase text-black">
            Craving <span className="text-[#D92525]">Chicken?</span>
            <br />
            KKFC Bangarpet
            <br />
            is your <span className="bg-black text-[#FFB800] px-2 inline-block">go-to</span> spot!
          </h1>

          <p className="mt-6 font-body text-lg md:text-xl text-neutral-800 max-w-2xl leading-relaxed">
            Crispy, juicy and full of flavour —{" "}
            <span className="font-semibold text-black">
              one bite and you'll keep coming back.
            </span>
          </p>

          {/* Quick stats */}
          <div className="mt-6 flex flex-wrap items-center gap-3 md:gap-5">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4].map((i) => (
                <Star
                  key={i}
                  size={18}
                  className="text-[#FFB800]"
                  fill="#FFB800"
                  strokeWidth={1}
                />
              ))}
              <Star
                size={18}
                className="text-[#FFB800]"
                fill="url(#half)"
                strokeWidth={1}
              />
              <span className="ml-2 font-bold text-black">4.1</span>
              <span className="text-sm text-neutral-600">/ 5 ratings</span>
            </div>
            <span className="hidden sm:inline text-neutral-400">|</span>
            <span className="text-sm font-semibold text-neutral-800">
              Open till late ⏰
            </span>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href={WA_LINK}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-btn"
              className="group inline-flex items-center justify-center gap-3 brutal-border brutal-shadow-lg bg-[#25D366] text-black font-bold px-7 py-4 text-base md:text-lg uppercase tracking-wide"
            >
              <MessageCircle size={22} strokeWidth={2.5} />
              Order on WhatsApp
            </a>
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="hero-call-btn"
              className="inline-flex items-center justify-center gap-3 brutal-border brutal-shadow-lg bg-[#D92525] text-white font-bold px-7 py-4 text-base md:text-lg uppercase tracking-wide"
            >
              <Phone size={22} strokeWidth={2.5} />
              Call Now
            </a>
          </div>

          <p className="mt-4 text-xs text-neutral-600 uppercase tracking-wider">
            ⚡ Quick reply • Home delivery min ₹300 + ₹45 delivery • Parcel ₹10 extra • Free street parking
          </p>
        </div>

        {/* Right visual */}
        <div className="lg:col-span-5 relative">
          <div className="relative max-w-md mx-auto lg:mx-0 lg:ml-auto">
            <div className="relative brutal-border brutal-shadow-lg overflow-hidden bg-[#FFB800] aspect-[4/5]">
              <img
                src="/assets/WhatsApp Image 2026-05-07 at 7.10.03 PM (7).jpeg"
                alt="KKFC Bangarpet crispy fried chicken"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </div>
            {/* Crispy sticker */}
            <div className="absolute -top-4 -right-4 md:-top-6 md:-right-6 w-24 h-24 md:w-28 md:h-28 bg-[#D92525] brutal-border rounded-full flex flex-col items-center justify-center rotate-[-12deg] z-10">
              <span className="font-display text-white text-xl md:text-2xl leading-none">CRISPY</span>
              <span className="font-display text-[#FFB800] text-2xl md:text-3xl leading-none">100%</span>
            </div>
            {/* Halal sticker */}
            <div className="absolute -bottom-5 -left-5 md:-bottom-7 md:-left-7 w-24 h-24 md:w-28 md:h-28 bg-[#FFB800] brutal-border rounded-full flex flex-col items-center justify-center rotate-[10deg] z-10">
              <span className="font-display text-black text-base md:text-lg leading-none">100%</span>
              <span className="font-display text-[#D92525] text-2xl md:text-3xl leading-none mt-0.5">HALAL</span>
              <span className="font-body text-[8px] md:text-[9px] font-bold text-black uppercase tracking-widest mt-0.5">Certified</span>
            </div>
          </div>

          {/* Floating chip */}
          <div className="hidden md:flex absolute -bottom-5 right-4 lg:right-0 brutal-border brutal-shadow bg-white px-4 py-3 items-center gap-3">
            <div className="w-9 h-9 bg-[#FFB800] brutal-border flex items-center justify-center">
              <Flame size={18} strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-display text-xl text-black">HOT & FRESH</div>
              <div className="text-[11px] text-neutral-700 uppercase tracking-widest">
                Made on order
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inline SVG defs for half star */}
      <svg width="0" height="0" className="absolute">
        <defs>
          <linearGradient id="half">
            <stop offset="50%" stopColor="#FFB800" />
            <stop offset="50%" stopColor="transparent" stopOpacity={0} />
          </linearGradient>
        </defs>
      </svg>
    </section>
  );
}
